import React from 'react';
import SafeIcon from '../../common/SafeIcon';
import { FiMapPin, FiClock, FiCalendar, FiExternalLink } from 'react-icons/fi';
import { motion } from 'framer-motion';

const getCategoryIcon = (jobType) => {
  const map = {
    'Bicycle': 'https://media-manager-c.questera.ai/greta-media/8004b964219a2ec88a1ae2cf7702d68f059f7f87c6ce7c7516e333ff7734f22eeed8a0cb5324bfb74e1bfd7e577adf83/images/aW1hZ2Uvd2VicA==/17864fd5972b920e535108a39b018fa0.webp',
    'Camp': 'https://media-manager-c.questera.ai/greta-media/8004b964219a2ec88a1ae2cf7702d68f059f7f87c6ce7c7516e333ff7734f22eeed8a0cb5324bfb74e1bfd7e577adf83/images/aW1hZ2Uvd2VicA==/502bec69d50cd553e79c0eb15c3f3ed0.webp',
    'Climb/Mountaineer': 'https://media-manager-c.questera.ai/greta-media/8004b964219a2ec88a1ae2cf7702d68f059f7f87c6ce7c7516e333ff7734f22eeed8a0cb5324bfb74e1bfd7e577adf83/images/aW1hZ2Uvd2VicA==/886ae8637049d87144161b0c23185881.webp',
    'Environmental': 'https://media-manager-c.questera.ai/greta-media/8004b964219a2ec88a1ae2cf7702d68f059f7f87c6ce7c7516e333ff7734f22eeed8a0cb5324bfb74e1bfd7e577adf83/images/aW1hZ2Uvd2VicA==/9312a3e1fe23808a036bd6b480168351.webp',
    'Medical/Rescue': 'https://media-manager-c.questera.ai/greta-media/8004b964219a2ec88a1ae2cf7702d68f059f7f87c6ce7c7516e333ff7734f22eeed8a0cb5324bfb74e1bfd7e577adf83/images/aW1hZ2Uvd2VicA==/c542563e4e3e7f93f35a0e5e64b9795e.webp',
    'Other Outdoor': 'https://media-manager-c.questera.ai/greta-media/8004b964219a2ec88a1ae2cf7702d68f059f7f87c6ce7c7516e333ff7734f22eeed8a0cb5324bfb74e1bfd7e577adf83/images/aW1hZ2Uvd2VicA==/61ccfe4ee3ad525fb4fca9153358f610.webp',
    'Snow/Winter': 'https://media-manager-c.questera.ai/greta-media/8004b964219a2ec88a1ae2cf7702d68f059f7f87c6ce7c7516e333ff7734f22eeed8a0cb5324bfb74e1bfd7e577adf83/images/aW1hZ2Uvd2VicA==/84ebc85ac834fa35fb0d9ecc04068ba2.webp',
    'Trek/Backpack': 'https://media-manager-c.questera.ai/greta-media/8004b964219a2ec88a1ae2cf7702d68f059f7f87c6ce7c7516e333ff7734f22eeed8a0cb5324bfb74e1bfd7e577adf83/images/aW1hZ2Uvd2VicA==/c9a0a86f246100e636c3d2fa165f3cd5.webp',
    'Water': 'https://media-manager-c.questera.ai/greta-media/8004b964219a2ec88a1ae2cf7702d68f059f7f87c6ce7c7516e333ff7734f22eeed8a0cb5324bfb74e1bfd7e577adf83/images/aW1hZ2Uvd2VicA==/17eadaae261453fa620d9bd49b9e2ddd.webp'
  };
  return map[jobType] || map['Other Outdoor'];
};

const JobCard = ({ job, onClick }) => {
  const orgName = job.profile?.org_name || 'Adventure Jobs';
  const locationsCount = job.locations?.length || 0;
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={onClick}
      className="group bg-white rounded-[32px] p-6 border border-stone-100 hover:border-emerald-500/30 hover:shadow-2xl hover:shadow-emerald-900/5 transition-all cursor-pointer relative overflow-hidden"
    >
      {/* Featured Badge */}
      {(job.is_featured || job.is_pinned) && (
        <div className="absolute top-0 right-0">
          <div className="bg-emerald-500 text-white text-[10px] font-black uppercase tracking-widest px-4 py-1 rounded-bl-2xl">
            Featured
          </div>
        </div>
      )}

      <div className="flex flex-col h-full">
        <div className="flex items-start gap-5 mb-6">
          <div className="w-16 h-16 rounded-2xl bg-stone-50 border border-stone-100 flex items-center justify-center overflow-hidden shrink-0 group-hover:scale-110 transition-transform duration-500 p-3">
            <img 
              src={getCategoryIcon(job.job_type)} 
              alt={`${job.job_type} category icon`} 
              className="w-full h-full object-contain"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-black text-stone-900 group-hover:text-emerald-600 transition-colors leading-tight mb-1 truncate">
              {job.title}
            </h3>
            <p className="text-stone-500 font-bold uppercase tracking-widest text-[10px]">
              {orgName}
            </p>
          </div>
        </div>

        <div className="space-y-3 mb-8">
          <div className="flex items-center text-stone-400 group-hover:text-stone-600 transition-colors">
            <SafeIcon icon={FiMapPin} className="mr-2 text-emerald-500" />
            <span className="text-[10px] font-black uppercase tracking-widest truncate">
              {job.locations?.[0]?.name || 'Remote'}
              {locationsCount > 1 && (
                <span className="text-emerald-500 ml-1 inline-block whitespace-nowrap">
                  + {locationsCount - 1} {locationsCount === 2 ? 'other' : 'others'}
                </span>
              )}
            </span>
          </div>
          <div className="flex items-center text-stone-400 group-hover:text-stone-600 transition-colors">
            <SafeIcon icon={FiClock} className="mr-2 text-emerald-500" />
            <span className="text-[10px] font-black uppercase tracking-widest">
              {job.category}
            </span>
          </div>
          <div className="flex items-center text-stone-400 group-hover:text-stone-600 transition-colors">
            <SafeIcon icon={FiCalendar} className="mr-2 text-emerald-500" />
            <span className="text-[10px] font-black uppercase tracking-widest">
              Posted {new Date(job.created_at).toLocaleDateString()}
            </span>
          </div>
        </div>

        <div className="mt-auto flex items-center justify-between pt-6 border-t border-stone-50">
          <div className="flex flex-wrap gap-2">
            {job.tags?.slice(0, 2).map((tag, idx) => (
              <span key={idx} className="bg-stone-50 text-stone-400 text-[8px] font-black uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border border-stone-100">
                {tag.tag}
              </span>
            ))}
          </div>
          <div className="bg-stone-900 text-white p-3 rounded-xl opacity-0 group-hover:opacity-100 translate-x-4 group-hover:translate-x-0 transition-all duration-300">
            <SafeIcon icon={FiExternalLink} />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default JobCard;